import firebase from 'firebase';
import _ from 'lodash';
import vCalendar from 'v-calendar';
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import { integer, required, required_if, confirmed, email, min, max, length } from 'vee-validate/dist/rules';
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import vmodal from 'vue-js-modal';
import VueMoment from 'vue-moment';
import vSelect from 'vue-select';
import VueSocketIO from 'vue-socket.io';
import VueToastr from 'vue-toastr';

import 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import '@/assets/css/main.css';
import '@/assets/css/safe.css';
import 'vue-select/dist/vue-select.css';
import { BootstrapVue } from 'bootstrap-vue';

import App from './App';
import { router } from './router';
import store from './store';
import { dateTimeFormats } from './services/translationService';
import messages from './translation/messages';
import { firebaseConfig } from '../configs/configFirebase.json';

// *** VALIDATION RULES ***
extend('confirmed', {
    ...confirmed,
    message: 'error_confirmation'
});
extend('custom_vnr_valid', {
    validate: value => {
        const digitsRegExp = /^\d+$/;
        return value[0].toLowerCase() === 'p' && (value.length < 2 || digitsRegExp.test(value.substring(1)));
    },
    message: 'error_custom_vnr_format'
});
extend('email', {
    ...email,
    message: 'error_valid_email'
});
extend('integer', {
    ...integer,
    message: 'error_must_be_integer'
});
extend('length', {
    ...length,
    message: 'error_wrong_length'
});
extend('max', {
    ...max,
    message: 'error_too_long'
});
extend('maxValue', {
    validate: (value, args) => {
        return value <= args.maxValue;
    },
    params: ['maxValue'],
    message: 'error_value_too_high'
});
extend('med_name_valid', {
    validate: (_value, args) => {
        return !args.searchDone || (args.searchDone && args.vnrOptions.length > 0);
    },
    params: ['searchDone', 'vnrOptions'],
    message: 'error_med_name_not_found'
});
extend('min', {
    ...min,
    message: 'error_too_short'
});
extend('minValue', {
    validate: (value, args) => {
        return value >= args.minValue;
    },
    params: ['minValue'],
    message: 'error_value_too_low'
});
extend('not_negative', {
    validate: value => {
        return value >= 0;
    },
    message: 'error_cannot_be_negative'
});
extend('positive', {
    validate: value => {
        return value >= 1;
    },
    message: 'error_not_positive'
});
extend('reservation_amount_required', {
    validate: (_value, args) => {
        return (
            args.vnrNameMatch !== true ||
            (args.vnrNameMatch === true &&
                ((args.thisField !== null && args.thisField !== '' && args.thisField !== '0' && args.thisField !== 0) ||
                    (args.otherField !== null &&
                        args.otherField !== '' &&
                        args.otherField !== '0' &&
                        args.otherField !== 0)))
        );
    },
    params: ['vnrNameMatch', 'thisField', 'otherField'],
    message: 'error_amount_required',
    computesRequired: true
});
extend('reservation_max_value', {
    validate: (value, args) => {
        return !args.vnrNameMatch || (args.vnrNameMatch && value <= args.maxValue);
    },
    params: ['vnrNameMatch', 'maxValue'],
    message: 'error_value_too_high'
});
extend('required', {
    ...required,
    message: 'error_field_required'
});
extend('required_if', {
    ...required_if,
    message: 'error_field_required'
});
extend('unique_prescription', {
    validate: (value, args) => {
        const amountFound = args.resPrescriptions.filter(
            prescription => prescription.toLowerCase().trim() === value.toLowerCase().trim()
        ).length;
        return amountFound <= args.allowedAmount;
    },
    params: ['allowedAmount', 'resPrescriptions'],
    message: 'error_prescription_not_unique'
});
extend('unique_vnr', {
    validate: (value, args) => {
        return !args.resVnrs.find(vnr => vnr === value);
    },
    params: ['resVnrs'],
    message: 'error_duplicate_vnr'
});
extend('vnr_in_used_meds', {
    validate: (_value, args) => {
        return args.vnrNameMatch !== false;
    },
    params: ['vnrNameMatch'],
    message: 'error_vnr_not_found_in_used_meds'
});
extend('vnr_valid', {
    validate: (_value, args) => {
        return args.vnrNameMatch !== false;
    },
    params: ['vnrNameMatch'],
    message: 'error_vnr_not_found'
});

Vue.use(VueI18n);
Vue.use(vmodal, {
    dialog: true,
    dynamic: true,
    dynamicDefaults: { clickToClose: false }
});
Vue.use(VueMoment);
Vue.use(vCalendar);
Vue.use(VueToastr);
Vue.use(BootstrapVue);

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
Vue.component('v-select', vSelect);

export const i18n = new VueI18n({
    locale: localStorage.getItem('locale') || 'fi',
    messages,
    dateTimeFormats
});

if (process.env.VUE_APP_ENABLE_SOCKET === 'true') {
    Vue.use(
        new VueSocketIO({
            debug: process.env.VUE_APP_SOCKET_DEBUG,
            connection: process.env.VUE_APP_NOTIFICATIONS_SOCK_URL
        })
    );
}

Vue.config.productionTip = false;

firebase.initializeApp(firebaseConfig);
let app;

// onAuthStateChanged needed for id token refresh
firebase.auth().onAuthStateChanged(_user => {
    if (!app) {
        app = new Vue({
            i18n,
            router,
            store,
            render: h => h(App),
            mounted() {
                this.$toastr.defaultPosition = 'toast-top-right';
            }
        }).$mount('#app');
    }
});
